import { useCallback, useState } from "react";
import type { ReactNode } from "react";
import { useDisconnect, type Connector } from "wagmi";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import {
  AppError,
  Button,
  FlexRow,
  IconCoinbase,
  IconMetamask,
  IconOkx,
  IconWalletConnect,
  Stack,
  Text,
} from "atoms";
import { preventForwarding } from "styles/styleUtils";
import {
  useConnectWallet,
  type ConnectHandler,
} from "../utils/useConnectWallet";

type ConnectWalletProps = {
  children?: React.ReactNode;
  /**
   * Optional function to toggle showing the Sign In by Email form
   *
   * If not provided, the Sign In by Email button is not displayed
   */
  showEmailSignIn?: VoidFunction;
  /**
   * Optional function to run after successfully connecting a wallet
   */
  onConnect?: ConnectHandler;
};

/** Main component for wallet connection flow */
function ConnectWallet({
  children,
  showEmailSignIn,
  onConnect,
  ...props
}: ConnectWalletProps) {
  const {
    connectors,
    error,
    isConnecting,
    isSigning,
    isConnected,
    address,
    activeConnector,
    connectAndSign,
    signMessage,
  } = useConnectWallet({ onConnect });

  return (
    <Wrapper {...props}>
      {children}

      {error && <AppError>{error}</AppError>}

      {isConnected ? (
        <ConnectedWallet
          address={address}
          connectorName={activeConnector?.name}
          isSigning={isSigning}
          onSign={signMessage}
        />
      ) : (
        <WalletConnectors
          connectors={connectors}
          isConnecting={isConnecting}
          onConnect={connectAndSign}
          showEmailSignIn={showEmailSignIn}
        />
      )}
    </Wrapper>
  );
}

/** Display for connected wallet with sign/disconnect options */
type ConnectedWalletProps = {
  address?: `0x${string}`;
  connectorName?: string;
  isSigning: boolean;
  onSign: () => void;
};

function ConnectedWallet({
  address,
  connectorName,
  isSigning,
  onSign,
}: ConnectedWalletProps) {
  const disconnect = useDisconnect();

  const handleDisconnect = () => {
    disconnect.mutate();
  };

  return (
    <FlexRow flexDirection="column" alignItems="center">
      <Stack>
        <Text as="span" bold>
          Connected to {connectorName} using address:
        </Text>
        <Text>{address}</Text>
      </Stack>

      <ConnectButton onClick={onSign} disabled={isSigning}>
        Sign Message
      </ConnectButton>

      <ConnectButton variant="secondary" onClick={handleDisconnect}>
        <ConnectIcon label={connectorName ?? ""} /> Disconnect
      </ConnectButton>
    </FlexRow>
  );
}

/** List of available wallet connectors */
type WalletConnectorsProps = {
  connectors: readonly Connector[];
  isConnecting: boolean;
  onConnect: (connector: Connector) => Promise<void>;
  showEmailSignIn?: VoidFunction;
};

function WalletConnectors({
  connectors,
  isConnecting,
  onConnect,
  showEmailSignIn,
}: WalletConnectorsProps) {
  const [pendingConnectorId, setPendingConnectorId] = useState<string | null>(
    null,
  );

  const handleClick = useCallback(
    async (connector: Connector) => {
      setPendingConnectorId(connector.id);

      try {
        await onConnect(connector);
      } finally {
        setPendingConnectorId(null);
      }
    },
    [onConnect],
  );

  return (
    <Buttons>
      {connectors.map((connector) => (
        <ConnectButton
          key={connector.id}
          variant="secondary"
          disabled={isConnecting}
          isConnecting={isConnecting && pendingConnectorId === connector.id}
          onClick={() => handleClick(connector)}
        >
          <ConnectIcon label={connector.name} /> {connector.name}
        </ConnectButton>
      ))}

      {showEmailSignIn && <EmailSignInOption onClick={showEmailSignIn} />}
    </Buttons>
  );
}

/** Email sign in option with separator */
function EmailSignInOption({ onClick }: { onClick: VoidFunction }) {
  return (
    <Stack>
      <FlexRow>
        <Dash />
        <Text textAlign="center" size="xxs" bold textTransform="uppercase">
          or
        </Text>
        <Dash />
      </FlexRow>

      <ConnectButton variant="secondary" onClick={onClick}>
        Sign In with Email
      </ConnectButton>
    </Stack>
  );
}

/** Wallet provider icon based on connector name */
function ConnectIcon({ label }: { label: string }) {
  const icons: Record<string, ReactNode> = {
    MetaMask: <IconMetamask />,
    "Coinbase Wallet": <IconCoinbase />,
    "OKX Wallet": <IconOkx />,
    WalletConnect: <IconWalletConnect />,
  };

  return <>{icons[label] ?? null}</>;
}

// Styled Components

const Wrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1rem",
  marginBottom: theme.spacing.lg,
}));

const loading = keyframes({
  "0%": {
    transform: "translateX(-100%)",
  },
  "66%, 100%": {
    transform: "translateX(200%)",
  },
});

type ConnectButtonProps = { isConnecting?: boolean };

const ConnectButton = styled(Button, {
  shouldForwardProp: preventForwarding<ConnectButtonProps>("isConnecting"),
})<ConnectButtonProps>(({ theme, isConnecting }) => ({
  fontSize: theme.fontSizes.sm,
  fontWeight: "bold",
  alignItems: "center",
  gap: 10,
  paddingLeft: "2rem",
  paddingRight: "2.2rem",
  overflow: "hidden",
  position: "relative",
  width: "100%",
  "&>svg": {
    width: "1em",
    height: "auto",
  },
  ...(isConnecting
    ? {
        borderColor: theme.colors.accent1,
        background: theme.colors.accent1LL,
        "&[disabled]": {
          opacity: 1,
        },
        "&::before": {
          animation: `${loading} 1.6s infinite`,
          content: "''",
          display: "block",
          top: 0,
          bottom: 0,
          left: 0,
          width: "50%",
          position: "absolute",
          backgroundImage:
            "linear-gradient(to right, rgb(0 0 0 / 0) 0%, rgb(255 255 255 / .4) 50%, rgb(0 0 0 / 0) 100%)",
        },
      }
    : {}),
}));

const Buttons = styled.div(({ theme }) => ({
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "center",
  "& > *": {
    width: "100%",
  },
  "& > * + *": {
    marginTop: theme.spacing.md,
  },
}));

const Dash = styled.span({
  height: 2,
  backgroundColor: "currentColor",
  opacity: 0.2,
  flex: 1,
  "&+p": { margin: 0 },
});

export type { ConnectHandler };
export { ConnectWallet, Dash, ConnectButton };
